/**
 * SessionHistory — полная история завершённых live-сессий хоста на маршруте
 * `/me/history` (Фаза 12, B4).
 *
 * Раньше история жила в кабинете `/me`: «Показать ещё» аппендил строки, DOM и кеш
 * inline-итогов росли неограниченно. Теперь кабинет показывает только последние 10
 * (Dashboard), а здесь — оконная пагинация (limit 20, «Назад/Вперёд» + номер
 * страницы): DOM ограничен одной страницей. Inline-итоги (accordion «Итоги» +
 * FinishScreen-подиум + ResultsBoard с кешом ответов) переехали сюда целиком.
 *
 * F5-persist: номер страницы живёт в query `?page=N` (по образцу `?pin=`/`?token=`,
 * читается в App.tsx при монтировании). Смена страницы — navigate() (pushState),
 * поэтому F5 и шаринг ссылки открывают ту же страницу. Бэкенд не меняется:
 * GET /api/me/sessions/history уже поддерживает limit/offset + total.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchMySessionHistory,
  fetchMySessionResults,
  type MySessionHistoryItem,
  type MySessionResults,
} from '../api';
import { FinishScreen } from '../components/FinishScreen';
import { ResultsBoard } from '../components/ResultsBoard';
import { navigate, redirect } from '../router';
import { useAuth } from '../useAuth';
import { errorMessage, HISTORY_PAGE_SIZE, historyDuration } from '../util';

export interface SessionHistoryProps {
  /**
   * Начальная страница (1-based) из query `?page=` — App читает её при монтировании
   * (F5-persist). Дальше экран сам управляет страницей и синхронизирует URL.
   */
  initialPage?: number;
}

export function SessionHistory({ initialPage = 1 }: SessionHistoryProps): JSX.Element {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [page, setPage] = useState(initialPage);
  const [items, setItems] = useState<MySessionHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline-раскрытие итогов (accordion): открытые строки + кэш ответов, чтобы
  // повторное раскрытие не слало новый запрос (§B4.2) — перенесено из Dashboard.
  const [openResultIds, setOpenResultIds] = useState<Set<number>>(new Set());
  const [resultsById, setResultsById] = useState<Record<number, MySessionResults>>({});
  const [resultsPendingIds, setResultsPendingIds] = useState<Set<number>>(new Set());
  const [resultsErrorById, setResultsErrorById] = useState<Record<number, string>>({});

  // Гейт: без auth → /login (как в кабинете; дубль эффекта App — экран
  // самодостаточен и тестируем вне диспетчера).
  useEffect(() => {
    if (!loading && !user) redirect('/login');
  }, [loading, user]);

  const refresh = useCallback(async (targetPage: number) => {
    setPageLoading(true);
    setError(null);
    try {
      const resp = await fetchMySessionHistory({
        limit: HISTORY_PAGE_SIZE,
        offset: (targetPage - 1) * HISTORY_PAGE_SIZE,
      });
      setItems(resp.items);
      setTotal(resp.total);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPageLoading(false);
    }
  }, []);

  // Загрузка при mount и смене страницы (оконная пагинация: одна страница в DOM).
  useEffect(() => {
    void refresh(page);
  }, [refresh, page]);

  const totalPages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  /**
   * Переход на страницу: обновляет state и URL (`?page=N`, pushState) — F5 и
   * шаринг ссылки открывают ту же страницу; «назад» браузера листает историю.
   */
  function goToPage(next: number): void {
    if (next < 1 || next > totalPages || next === page) return;
    setPage(next);
    setOpenResultIds(new Set());
    navigate(`/me/history?page=${next}`);
  }

  // «Итоги»/«Свернуть»: inline-раскрытие под строкой. При первом раскрытии —
  // ленивый fetchMySessionResults с кэшем (повторное — без запроса); ошибка
  // показывается внутри блока, строка остаётся (как было в кабинете).
  async function handleToggleResults(s: MySessionHistoryItem): Promise<void> {
    const open = openResultIds.has(s.id);
    setOpenResultIds((prev) => {
      const next = new Set(prev);
      if (open) {
        next.delete(s.id);
      } else {
        next.add(s.id);
      }
      return next;
    });
    if (open || resultsById[s.id] !== undefined || resultsPendingIds.has(s.id)) {
      return;
    }
    setResultsPendingIds((prev) => new Set(prev).add(s.id));
    setResultsErrorById((prev) => {
      const next = { ...prev };
      delete next[s.id];
      return next;
    });
    try {
      const res = await fetchMySessionResults(s.id);
      setResultsById((prev) => ({ ...prev, [s.id]: res }));
    } catch (e) {
      setResultsErrorById((prev) => ({ ...prev, [s.id]: errorMessage(e) }));
    } finally {
      setResultsPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  }

  // Пока идёт bootstrap либо пользователя нет — заглушка (редирект стартует
  // в эффекте выше после завершения bootstrap).
  if (loading || !user) {
    return (
      <main className="card card--page">
        <p className="muted" data-testid="me-history-gate">
          {loading ? t('common.loading') : t('dashboard.gateGuest')}
        </p>
      </main>
    );
  }

  return (
    <main
      className="card card--wide"
      aria-labelledby="me-history-title"
      data-testid="session-history"
    >
      <header className="screen-head">
        <h2 id="me-history-title" className="block-title">
          {t('dashboard.history.title')}
        </h2>
        <div className="screen-head-meta">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => navigate('/me')}
            data-testid="history-back"
          >
            {t('dashboard.history.backToMe')}
          </button>
        </div>
      </header>

      <section className="block">
        <div className="block-head">
          <h3 className="block-subtitle">{t('dashboard.history.fullTitle')}</h3>
          <div className="block-head-meta">
            <button
              type="button"
              className="link-btn"
              onClick={() => void refresh(page)}
              disabled={pageLoading}
            >
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {pageLoading ? (
          <p className="muted" data-testid="me-history-loading">
            {t('dashboard.history.loading')}
          </p>
        ) : error ? (
          <p className="error" role="alert" data-testid="me-history-error">
            {error}
          </p>
        ) : items.length === 0 ? (
          <p className="muted" data-testid="me-history-empty">
            {t('dashboard.history.empty')}
          </p>
        ) : (
          <ul className="game-list" data-testid="me-history-list">
            {items.map((s) => {
              const open = openResultIds.has(s.id);
              const detail = resultsById[s.id];
              const detailError = resultsErrorById[s.id];
              return (
                <li
                  key={s.id}
                  className={`game-row dashboard-game${open ? ' dashboard-game--open' : ''}`}
                  data-testid="me-history-row"
                >
                  <span className="dashboard-game__title" data-testid="me-history-title">
                    {s.gameTitle}
                  </span>
                  <span className="muted" data-testid="me-history-date">
                    {new Date(s.finishedAt).toLocaleString()}
                  </span>
                  <span className="muted" data-testid="me-history-players">
                    {t('dashboard.history.players', { n: s.playerCount })}
                  </span>
                  <span className="muted" data-testid="me-history-duration">
                    {historyDuration(t, s.durationSec)}
                  </span>
                  <div className="dashboard-game__actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void handleToggleResults(s)}
                      data-testid="me-history-results"
                    >
                      {open ? t('dashboard.history.hide') : t('dashboard.history.results')}
                    </button>
                  </div>
                  {open ? (
                    <div className="history-detail" data-testid="me-history-detail">
                      {detailError ? (
                        <p className="error" role="alert" data-testid="me-history-detail-error">
                          {detailError}
                        </p>
                      ) : detail ? (
                        <>
                          <FinishScreen
                            entries={detail.players.map((p) => ({
                              nick: p.nickname,
                              score: p.score,
                            }))}
                            title={t('dashboard.history.resultsTitle', { title: s.gameTitle })}
                            subtitle={`${new Date(detail.finishedAt).toLocaleString()} · ${historyDuration(t, detail.durationSec)}`}
                          />
                          <ResultsBoard results={detail} />
                        </>
                      ) : (
                        <p className="muted" data-testid="me-history-detail-loading">
                          {t('common.loading')}
                        </p>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {/* Оконная пагинация: Назад/Вперёд + номер страницы. Кнопки на границах
            disabled; DOM ограничен одной страницей (без «Показать ещё»). */}
        {total > 0 ? (
          <nav className="history-pagination" aria-label={t('dashboard.history.title')}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || pageLoading}
              data-testid="history-prev"
            >
              {t('dashboard.history.prev')}
            </button>
            <span className="muted" data-testid="history-page-info">
              {t('dashboard.history.page', { page, total: totalPages })}
            </span>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || pageLoading}
              data-testid="history-next"
            >
              {t('dashboard.history.next')}
            </button>
          </nav>
        ) : null}
      </section>
    </main>
  );
}
